// 261010 P8b-2 — chính sách chuông (thuần: không antd/router). CTA chỉ điều hướng / không-AI. SOURCE_COVERAGE_GAP = INFO, không vào badge.
import type { WorkflowAlert, WorkflowSourceCoverage, WorkflowStepKey } from '../../types/filmWorkflow';
import { sortAlerts } from './workflowBoardUtils';

export const SOURCE_COVERAGE_CODE = 'SOURCE_COVERAGE_GAP';
export const BELL_MAX_ITEMS = 50;
export const BELL_BADGE_CAP = 99;

export type BellNav =
  | { type: 'OPEN_EVENT'; eventId: string; step: WorkflowStepKey }
  | { type: 'OPEN_FIRST'; step: WorkflowStepKey }
  /** `anchor` = id phần tử cuộn tới sau khi mở bước (vd. `wf-step-flags`). */
  | { type: 'OPEN_BOARD'; step: WorkflowStepKey; anchor?: string }
  | { type: 'SCROLL_RUN' }
  | { type: 'DISMISS'; jobId: string }
  | { type: 'RELEASE'; jobId: string }
  | { type: 'TOGGLE_SWALLOWED' };

export interface BellButton { key: string; label: string; action: BellNav; kind?: 'primary' | 'ghost' }

export interface BellCtx {
  firstEventId: string | null;
  dismissedJobIds: string[];
  jobsCount: number;
  sourceCoverage?: WorkflowSourceCoverage;
}

export interface VisibleBellItem {
  kind: 'alert' | 'jobs';
  alert?: WorkflowAlert;
  buttons: BellButton[];
  jobsCount?: number;
  swallowed?: Array<{ line: number; text: string }>;
  swallowedMore?: number;
  coverageNote?: string;
}

export interface VisibleBell {
  items: VisibleBellItem[];
  hiddenCount: number;
  badge: number;
  badgeText: string;
  total: number;
  empty: boolean;
}

const AI_RE = /Dựng lại|Đồng bộ lại|Chạy lại|Tạo video|Gen /i;

export function isAiBellAction(b: BellButton): boolean {
  if (AI_RE.test(b.label)) return true;
  const t = b.action.type;
  return t !== 'OPEN_EVENT' && t !== 'OPEN_FIRST' && t !== 'OPEN_BOARD' && t !== 'SCROLL_RUN' && t !== 'DISMISS' && t !== 'RELEASE' && t !== 'TOGGLE_SWALLOWED';
}

const openEvent = (alert: WorkflowAlert): BellButton | null =>
  alert.subjectId ? { key: 'open', label: 'Mở bước', action: { type: 'OPEN_EVENT', eventId: alert.subjectId, step: alert.stepKey } } : null;

const NAV_OPEN = new Set([
  'PROMPT_MODE_MISMATCH', 'PROMPT_WARNING', 'PROMPT_STYLE_SCRUB',
  'STORYBOARD_STALE', 'STORYBOARD_DRIFT', 'MAP_STALE', 'MAP_LINK_DRIFT', 'CLIP_FAILED',
]);

export function bellActionOf(alert: WorkflowAlert, ctx: BellCtx): BellButton[] {
  const code = alert.code;
  if (code === 'STEP_REVIEW_NEEDS_DECISION') {
    return alert.subjectId
      ? [{ key: 'review', label: 'Mở phiếu', action: { type: 'OPEN_EVENT', eventId: alert.subjectId, step: alert.stepKey } }]
      : [];
  }
  if (code === 'JOB_FAILED') {
    const out: BellButton[] = [];
    if (alert.subjectId) out.push({ key: 'open', label: 'Mở video', action: { type: 'OPEN_EVENT', eventId: alert.subjectId, step: alert.stepKey } });
    if (alert.ref?.jobId) out.push({ key: 'skip', label: 'Bỏ qua', action: { type: 'DISMISS', jobId: alert.ref.jobId }, kind: 'ghost' });
    return out;
  }
  if (code === 'JOB_EXPIRED') {
    return alert.ref?.jobId
      ? [{ key: 'release', label: 'Gỡ khoá', action: { type: 'RELEASE', jobId: alert.ref.jobId } }]
      : [];
  }
  if (NAV_OPEN.has(code) || code.startsWith('NATURAL_CAMERA_') || code.startsWith('WORKFLOW_RUN_FLAG:')) {
    const b = openEvent(alert);
    return b ? [b] : [];
  }
  if (code === 'CANONICAL_NOT_APPROVED' || code === 'STYLE_PROFILE_NOT_APPROVED' || code === 'ASSETS_STALE') {
    return ctx.firstEventId
      ? [{ key: 'assets', label: 'Mở Tài sản', action: { type: 'OPEN_FIRST', step: 'ASSETS' } }]
      : [];
  }
  if (code === 'EXPORT_STALE' || code === 'EXPORT_FAILED') {
    // UI1a: khung mới bỏ bước Ghép — chuông dẫn tới bước Video (video-ui 29/10: khối CapCut đã bỏ, clip nằm trên từng dòng video). Route / UI Ghép cũ vẫn còn.
    return [{ key: 'asm', label: 'Mở bước Video', action: { type: 'OPEN_BOARD', step: 'VIDEO' } }];
  }
  if (code === 'SCRIPT_APPROVAL_OUTDATED') {
    return [{ key: 'reapprove', label: 'Duyệt lại', action: { type: 'SCROLL_RUN' } }];
  }
  if (code === SOURCE_COVERAGE_CODE) {
    const out: BellButton[] = [{ key: 'sw', label: 'Xem dòng bị bỏ', action: { type: 'TOGGLE_SWALLOWED' }, kind: 'ghost' }];
    if (ctx.firstEventId) out.push({ key: 'script', label: 'Mở Phân đoạn', action: { type: 'OPEN_FIRST', step: 'SCRIPT' } });
    return out;
  }
  return [];
}

export function visibleBellOf(alerts: WorkflowAlert[], ctx: BellCtx): VisibleBell {
  const dismissed = new Set(ctx.dismissedJobIds);
  const kept = sortAlerts(alerts.filter((a) => a.code !== 'CANONICAL_NOT_APPROVED' && !(a.code === 'JOB_FAILED' && a.ref?.jobId && dismissed.has(a.ref.jobId))));
  const badge = kept.filter((a) => a.severity === 'ERROR' || a.severity === 'WARN').length;
  const hiddenCount = Math.max(0, kept.length - BELL_MAX_ITEMS);
  const shown = kept.slice(0, BELL_MAX_ITEMS);
  const items: VisibleBellItem[] = [];
  if (ctx.jobsCount > 0) items.push({ kind: 'jobs', jobsCount: ctx.jobsCount, buttons: [] });
  for (const alert of shown) {
    const item: VisibleBellItem = { kind: 'alert', alert, buttons: bellActionOf(alert, ctx) };
    if (alert.code === SOURCE_COVERAGE_CODE) {
      const cov = ctx.sourceCoverage;
      item.swallowed = cov?.swallowed ?? [];
      item.swallowedMore = Math.max(0, (cov?.swallowedCount ?? 0) - (cov?.swallowed.length ?? 0));
      item.coverageNote = 'tính lúc tạo plan; chỉ có số dòng, không có số chương'
        + (item.swallowedMore ? `; còn ${item.swallowedMore} dòng nữa` : '');
    }
    items.push(item);
  }
  return {
    items, hiddenCount, badge,
    badgeText: badge > BELL_BADGE_CAP ? '99+' : String(badge),
    total: kept.length + (ctx.jobsCount > 0 ? 1 : 0),
    empty: kept.length === 0 && ctx.jobsCount === 0,
  };
}

export function planStepAlertsOf(alerts: WorkflowAlert[], step: WorkflowStepKey): WorkflowAlert[] {
  return alerts.filter((a) => a.subjectType === 'PLAN' && a.stepKey === step && a.code !== SOURCE_COVERAGE_CODE && a.code !== 'CANONICAL_NOT_APPROVED');
}
